import * as React from "react"
import { Dialog, Transition } from "@headlessui/react"
import { XMarkIcon } from "@heroicons/react/24/outline"

import { cn } from "@/utils"

export interface DrawerProps {
  /** Whether the drawer is visible */
  open: boolean
  /** Called when the drawer should close */
  onClose: () => void
  /** Drawer title */
  title?: React.ReactNode
  /** Slide-in direction */
  placement?: "left" | "right"
  /** Width class */
  width?: string
  /** Additional className for the drawer panel */
  className?: string
  children: React.ReactNode
}

function Drawer({
  open,
  onClose,
  title,
  placement = "right",
  width = "max-w-md",
  className,
  children,
}: DrawerProps) {
  const translateFrom = placement === "right" ? "translate-x-full" : "-translate-x-full"
  const panelPosition = placement === "right" ? "ml-auto" : "mr-auto"

  return (
    <Transition appear show={open} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom={translateFrom}
            enterTo="translate-x-0"
            leave="ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo={translateFrom}
          >
            <Dialog.Panel
              className={cn(
                "fixed inset-y-0 w-full flex flex-col bg-popover shadow-xl",
                width,
                placement === "right" ? "right-0" : "left-0",
                panelPosition,
                className
              )}
            >
              {/* Header */}
              {title && (
                <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                  <Dialog.Title className="text-lg font-semibold text-foreground">
                    {title}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              )}

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-6">{children}</div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}

export { Drawer }
